import {
  CategoryOptionsInterface,
  ContractTypes,
  ContractTypesInterface,
  CourseSegment,
  CourseSegmentOptions,
  CourseSegmentOptionsInterface,
  getCategoryOptions,
  isCourseSegment,
  WorkSchedule,
} from '@duke-hq/shared-constants/enrolments';
import { Controller, useController, useFormContext, useWatch } from 'react-hook-form';
import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Input,
  Skeleton,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { AsyncSelect, MultiValue, Select, SingleValue } from 'chakra-react-select';
import { useMemo, useCallback } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { djs } from '@duke-hq/djs';
import { AlertCircleIcon } from 'lucide-react';
import { useTenantContext } from '@duke-hq/app-provider-web/tenant/TenantContext';
import { useAuthTenant } from '../useAuthTenant';
import { useDebounceCallback } from 'usehooks-ts';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { Option } from '../../components/Option';
import { courseHasSemesterTwo } from '@duke-hq/shared-constants/course';
import { CourseKindSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseKind.schema';
import {
  ConsultantOption,
  ConsultantRoleSchema,
  SiteOption,
} from '@duke-hq/svc-api/provider/util/consultants';
import { States } from '@duke-hq/svc-api-db/schemas/enums/States.schema';

export function EnrolmentSettings({
  course,
  expectedCompletionDate,
}: {
  course: { kind: string; id: string };
  expectedCompletionDate?: string;
}) {
  const { data: courseSchedule } = trpcTenant.courses.courseSchedule.useQuery(course.id);
  const auth = useAuthTenant();
  const tenantCtx = useTenantContext();
  const {
    register,
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<{
    site_location_id: string;
    contract_category: string;
    consultant_id: string;
    start_at_date: Date;
    external_id: string;
    enrolment_contract_type: string;
    course_segment: string;
    work_schedule: WorkSchedule;
  }>();

  const [work_schedule, site_location_id, enrolment_contract_type, course_segment] = useWatch({
    control,
    name: ['work_schedule', 'site_location_id', 'enrolment_contract_type', 'course_segment'],
  });

  const { data: sitesList, isFetching: isSitesLoading } =
    trpcTenant.locations.listUserSiteAccess.useQuery({
      type: 'site',
    });

  const { data: consultantsInSite, isFetching: isConsultantsLoading } =
    trpcTenant.locations.getConsultantsInSiteLocation.useQuery(
      { siteLocationIds: [site_location_id] },
      {
        enabled:
          !!site_location_id && tenantCtx?.permissions.includes('canCreateReferralsForTeamMembers'),
        initialData: [],
      },
    );

  const sitesOptions = useMemo(() => {
    return (sitesList
      ?.map((site) => ({
        value: {
          site_id: site.id,
          site_name: site.name,
          site_state: site.state,
        },
        label: site.state ? `${site.name}, ${site.state}` : site.name,
      }))
      .filter((option: unknown): option is SiteOption => option !== null) ||
      []) satisfies SiteOption[];
  }, [sitesList]);

  const loadSitesOptions = useDebounceCallback<
    (inputValue: string, callback: (opts: SiteOption[]) => void) => void
  >((inputValue: string, callback) => {
    if (sitesOptions.length > 0) {
      callback(
        sitesOptions
          .filter(
            (option) =>
              option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
              option.value.site_name.toLowerCase().includes(inputValue.toLowerCase()),
          )
          .filter((option): option is SiteOption => option !== null),
      );

      // If there's only one site available, select it
      if (!site_location_id && sitesOptions.length === 1) {
        if (sitesOptions?.[0]) {
          setValue('site_location_id', sitesOptions[0].value.site_id);
        }
      }
    } else {
      callback([]);
    }
  }, 500);

  const consultantOptions: ConsultantOption[] = useMemo(() => {
    return consultantsInSite
      .map((user) => {
        if (user.role === undefined) {
          return null;
        }
        return {
          role: ConsultantRoleSchema.safeParse(user.role).data,
          email: user.email ?? '',
          value: user.id,
          label: `${user.first_name} ${user.last_name} ${
            auth.user.id === user.id ? `(${t`myself`})` : ''
          }`,
          fullname: `${user.first_name} ${user.last_name}`,
        };
      })
      .filter((option): option is ConsultantOption => option !== null);
  }, [auth.user.id, consultantsInSite]);

  const loadConsultantsOptions = useDebounceCallback<
    (inputValue: string, callback: (opts: ConsultantOption[]) => void) => void
  >((inputValue, callback) => {
    if (consultantOptions.length > 0) {
      callback(
        consultantOptions.filter(
          (option) =>
            option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
            option.email?.toLowerCase().includes(inputValue.toLowerCase()),
        ),
      );
    } else {
      callback([]);
    }
  }, 500);

  const today = djs();
  const isFullTime = work_schedule === WorkSchedule.FULL_TIME;

  const {
    field: { value: contractType, onChange: onContractTypeChange },
  } = useController({
    name: 'enrolment_contract_type',
    control,
  });

  const categoryOptions = getCategoryOptions(contractType);

  const hasSemesterTwo = courseHasSemesterTwo(courseSchedule?.schedule ?? []);

  const handleContractTypeChange = useCallback(
    (val: SingleValue<ContractTypesInterface>) => {
      const selectedContractType = val?.value;
      if (typeof selectedContractType !== 'string') {
        return;
      }

      onContractTypeChange(selectedContractType);

      // Default logic for all contract types
      setValue('course_segment', CourseSegment.FULL_COURSE);

      // Overwrite defaults for specific contract types
      if (selectedContractType === 'Workforce Australia') {
        if (course.kind === CourseKindSchema.enum.QUALIFICATION) {
          setValue('course_segment', CourseSegment.FIRST_SEMESTER_ONLY);
        }
      } else if (selectedContractType === 'IEA') {
        // IEA requires full-time enrolment pace
        setValue('work_schedule', WorkSchedule.FULL_TIME);
      }

      const newCategoryOptions = getCategoryOptions(selectedContractType);

      // Always reset contract_category based on available options
      if (newCategoryOptions.length === 1 && typeof newCategoryOptions[0]?.value === 'string') {
        setValue('contract_category', newCategoryOptions[0].value);
      } else if (newCategoryOptions.length === 0) {
        setValue('contract_category', 'n/a');
      } else {
        setValue('contract_category', '');
      }
    },
    [onContractTypeChange, setValue, course.kind],
  );

  return (
    <Box style={{ width: '100%', height: '100%' }}>
      <FormControl isInvalid={!!errors.site_location_id} isRequired>
        <FormLabel htmlFor="site_location_id">
          <Trans>Site</Trans>
        </FormLabel>
        {isSitesLoading ? (
          <Skeleton height="32px" />
        ) : (
          <Controller
            key={site_location_id}
            name="site_location_id"
            control={control}
            rules={{
              required: t`Please select a site.`,
              validate: (value) =>
                typeof value === 'string' && value.trim() !== '' ? true : t`Please select a site.`,
            }}
            render={({ field }) => (
              <AsyncSelect
                id="site_location_id"
                name="site_location_id"
                placeholder={t`Select site`}
                defaultOptions
                loadOptions={loadSitesOptions}
                selectedOptionColorScheme={'brand'}
                value={sitesOptions?.find((option) => option.value?.site_id === field.value)}
                onChange={(
                  selectedOption:
                    | MultiValue<{
                        value: {
                          site_id: string;
                          site_name: string;
                          site_state: States | undefined;
                        };
                        label: string;
                      }>
                    | SingleValue<{
                        value: {
                          site_id: string;
                          site_name: string;
                          site_state: States | undefined;
                        };
                        label: string;
                      }>,
                ) => {
                  if (
                    selectedOption != null &&
                    'value' in selectedOption &&
                    selectedOption?.value != null
                  ) {
                    field.onChange(selectedOption?.value?.site_id ?? '');

                    if (
                      sitesOptions.length > 1 &&
                      tenantCtx?.permissions.includes('canCreateReferralsForTeamMembers') === true
                    ) {
                      setValue('consultant_id', '');
                    }
                  }
                }}
              />
            )}
          />
        )}
        <FormErrorMessage>{errors.site_location_id?.message}</FormErrorMessage>
      </FormControl>

      {tenantCtx?.permissions.includes('canCreateReferralsForTeamMembers') === true &&
      site_location_id.length > 0 ? (
        <FormControl isInvalid={!!errors.consultant_id} isRequired>
          <FormLabel htmlFor="consultant_id">
            <Trans>Consultant</Trans>
          </FormLabel>
          {isConsultantsLoading ? (
            <Skeleton height="32px" />
          ) : (
            <Controller
              key={getValues('site_location_id')}
              name="consultant_id"
              control={control}
              rules={{
                required: t`Please select a consultant.`,
                validate: (value) =>
                  typeof value === 'string' && value.trim() !== ''
                    ? true
                    : t`Please select a consultant.`,
              }}
              render={({ field }) => (
                <AsyncSelect
                  id="consultant_id"
                  name="consultant_id"
                  placeholder={t`Select consultant`}
                  defaultOptions
                  loadOptions={loadConsultantsOptions}
                  selectedOptionColorScheme={'brand'}
                  components={{ Option }}
                  value={consultantOptions?.find((option) => option.value === field.value) ?? null}
                  onChange={(
                    selectedOption: MultiValue<ConsultantOption> | SingleValue<ConsultantOption>,
                  ) => {
                    if (
                      selectedOption != null &&
                      'value' in selectedOption &&
                      selectedOption?.value != null
                    ) {
                      field.onChange(selectedOption?.value ?? '');
                    }
                  }}
                  defaultValue={consultantOptions.find((option) => option.value === auth.user.id)}
                />
              )}
            />
          )}
          <FormErrorMessage>{errors.consultant_id?.message}</FormErrorMessage>
        </FormControl>
      ) : null}

      <HStack w="full" justify="space-between" align="start" gap="10px">
        <FormControl isInvalid={!!errors.start_at_date} isRequired>
          <FormLabel htmlFor="start_at_date">
            <Trans>Start date</Trans>
          </FormLabel>
          <Input
            id="start_at_date"
            autoComplete="off"
            type="date"
            min={djs(today).format('YYYY-MM-DD')}
            {...register('start_at_date', {
              required: t`Start date is required.`,
              min: {
                value: djs(today).format('YYYY-MM-DD'),
                message: t`Please enter a valid start date. The date should not be in the past.`,
              },
              validate: (value) => djs(value).isValid() || t`Please enter a valid date.`,
            })}
          />
          <FormErrorMessage>{errors.start_at_date?.message}</FormErrorMessage>
        </FormControl>

        <FormControl
          isInvalid={!!errors.external_id}
          isRequired={tenantCtx?.settings.is_external_id_field_mandatory === true}
        >
          <FormLabel htmlFor="external_id">
            <Trans>Purchase order</Trans>
          </FormLabel>
          <Input
            id="external_id"
            {...register('external_id', {
              required:
                tenantCtx?.settings.is_external_id_field_mandatory === true &&
                t`This field is required.`,
              setValueAs: (value: string) => {
                if (typeof value === 'string') {
                  const trimmed = value.trim();
                  return trimmed === '' ? undefined : trimmed;
                }
                return value;
              },
            })}
            type="text"
            spellCheck="false"
            autoComplete="off"
            onChange={(e) => {
              setValue('external_id', e.target.value);
            }}
          />
          <FormErrorMessage>{errors.external_id?.message}</FormErrorMessage>
        </FormControl>
      </HStack>

      <HStack w="full" justify="space-between" align="start" gap="10px">
        <FormControl isInvalid={!!errors.enrolment_contract_type} isRequired>
          <FormLabel htmlFor="enrolment_contract_type">
            <Trans>Contract type</Trans>
          </FormLabel>
          <Controller
            name="enrolment_contract_type"
            control={control}
            rules={{ required: t`Contract type is required.` }}
            render={({ field }) => (
              <Select
                id="enrolment_contract_type"
                placeholder={t`Select option`}
                options={ContractTypes}
                value={ContractTypes.find((option) => option.value === field.value)}
                onChange={handleContractTypeChange}
              />
            )}
          />
          <FormErrorMessage>{errors.enrolment_contract_type?.message}</FormErrorMessage>
        </FormControl>

        {enrolment_contract_type && categoryOptions.length > 0 ? (
          <FormControl
            isInvalid={!!errors.contract_category}
            isRequired
            isDisabled={categoryOptions.length < 1}
          >
            <FormLabel htmlFor="contract_category">
              <Trans>Contract category</Trans>
            </FormLabel>
            <Controller
              name="contract_category"
              control={control}
              rules={{ required: t`Contract category is required.` }}
              render={({ field }) => (
                <Select
                  key={getValues('contract_category')}
                  id="contract_category"
                  placeholder={t`Select option`}
                  options={categoryOptions}
                  value={categoryOptions.find((option) => option.value === field.value)}
                  onChange={(
                    selectedOption:
                      | MultiValue<CategoryOptionsInterface>
                      | SingleValue<CategoryOptionsInterface>,
                  ) => {
                    if (
                      selectedOption != null &&
                      'value' in selectedOption &&
                      selectedOption?.value != null
                    ) {
                      field.onChange(selectedOption?.value);
                    }
                  }}
                  isReadOnly={categoryOptions.length === 1}
                />
              )}
            />
            <FormErrorMessage>{errors.contract_category?.message}</FormErrorMessage>
          </FormControl>
        ) : (
          <FormControl />
        )}
      </HStack>

      <HStack w="full" justify="space-between" align="start" gap="10px">
        {hasSemesterTwo ? (
          <FormControl isInvalid={!!errors.course_segment} isRequired>
            <FormLabel htmlFor="course_segment">
              <Trans>Course segment</Trans>
            </FormLabel>
            <Controller
              name="course_segment"
              control={control}
              rules={{ required: t`Course segment is required.` }}
              render={({ field }) => (
                <Select
                  id="course_segment"
                  placeholder={t`Select option`}
                  options={CourseSegmentOptions}
                  isDisabled={
                    course.kind === CourseKindSchema.enum.QUALIFICATION &&
                    (enrolment_contract_type === 'IEA' || !enrolment_contract_type)
                  }
                  value={
                    isCourseSegment(course_segment)
                      ? CourseSegmentOptions.find((option) => option.value === course_segment)
                      : undefined
                  }
                  onChange={(
                    selectedOption:
                      | MultiValue<CourseSegmentOptionsInterface>
                      | SingleValue<CourseSegmentOptionsInterface>,
                  ) => {
                    if (
                      selectedOption != null &&
                      'value' in selectedOption &&
                      selectedOption?.value != null
                    ) {
                      field.onChange(selectedOption?.value);
                    }
                  }}
                />
              )}
            />
            <FormErrorMessage>{errors.course_segment?.message}</FormErrorMessage>
          </FormControl>
        ) : null}

        <FormControl isRequired>
          <FormLabel>
            <Trans>Enrolment pace</Trans>
          </FormLabel>
          <HStack align="center">
            <ButtonGroup spacing={0} isAttached>
              <Button
                colorScheme={isFullTime ? 'brand' : 'gray'}
                onClick={() => {
                  setValue('work_schedule', WorkSchedule.FULL_TIME);
                }}
                variant={isFullTime ? 'solid' : 'ghost'}
                isDisabled={
                  course.kind === CourseKindSchema.enum.QUALIFICATION &&
                  (enrolment_contract_type === 'IEA' || !enrolment_contract_type)
                }
              >
                <Trans>Full-time</Trans>
              </Button>
              <Button
                colorScheme={!isFullTime ? 'brand' : 'gray'}
                onClick={() => {
                  setValue('work_schedule', WorkSchedule.PART_TIME);
                }}
                variant={!isFullTime ? 'solid' : 'ghost'}
                isDisabled={
                  course.kind === CourseKindSchema.enum.QUALIFICATION &&
                  (enrolment_contract_type === 'IEA' || !enrolment_contract_type)
                }
              >
                <Trans>Part-time</Trans>
              </Button>
            </ButtonGroup>
            {!isFullTime && (
              <Tooltip
                label={
                  <Box
                    bg="bg.surface"
                    borderRadius="8px"
                    color="fg.default"
                    borderWidth="1px"
                    borderColor="border.default"
                    p="8px"
                    fontSize="sm"
                  >
                    <Trans>Please note, by selecting part-time no outcome can be claimed.</Trans>
                  </Box>
                }
                fontSize="md"
                colorScheme="warning"
                placement="bottom"
                gutter={16}
                bg="transparent"
                shadow="none"
              >
                <Icon as={AlertCircleIcon} color="warning.emphasis" boxSize="20px" />
              </Tooltip>
            )}
          </HStack>
        </FormControl>
      </HStack>

      {expectedCompletionDate ? (
        <Text
          color="fg.emphasis"
          textStyle="sm"
          textAlign="left"
          fontWeight="normal"
          w="full"
          mt={6}
        >
          <Trans>Course is expected to end by</Trans> {expectedCompletionDate}
        </Text>
      ) : null}
    </Box>
  );
}
